import PropTypes from 'prop-types';
import { GoogleMap, useLoadScript, MarkerF } from "@react-google-maps/api";
import { useEffect, useState, useRef } from "react";
import { Box, Typography, Button, Tooltip, Fab } from "@mui/material";
import LoadingComponent from "./LoadingComponent";
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { theme } from "../../theme";

const containerStyle = {
    width: "100%",
    height: "100%",
    borderRadius: "15px",
};

const defaultLocation = { lat: 40.73061, lng: -73.935242 }; // NYC fallback

function ExploreMapView({ height = { xs: 600, sm: 450, md: 600 }, markers = [], onSearchArea, landingLocation }) {
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY,
    });

    const [location, setLocation] = useState(null);
    const [mapCenter, setMapCenter] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showSearchButton, setShowSearchButton] = useState(false);

    const mapRef = useRef(null);

    const getUserLocation = async () => {
        if ("geolocation" in navigator) {
            return new Promise((resolve) => {
                navigator.geolocation.getCurrentPosition(
                    (pos) => {
                        resolve({
                            lat: pos.coords.latitude,
                            lng: pos.coords.longitude,
                            method: "gps",
                        });
                    },
                    async () => {
                        try {
                            const fallback = await fetch("https://ipapi.co/json/");
                            const data = await fallback.json();
                            resolve({
                                lat: data.latitude,
                                lng: data.longitude,
                                method: "ip",
                            });
                        } catch (error) {
                            resolve({ ...defaultLocation, method: "fallback" });
                        }
                    }
                );
            });
        } else {
            try {
                const fallback = await fetch("https://ipapi.co/json/");
                const data = await fallback.json();
                return {
                    lat: data.latitude,
                    lng: data.longitude,
                    method: "ip",
                };
            } catch (error) {
                return { ...defaultLocation, method: "fallback" };
            }
        }
    };

    const handleRecenter = async () => {
        const loc = await getUserLocation();
        setLocation({ lat: loc.lat, lng: loc.lng });
        setMapCenter({ lat: loc.lat, lng: loc.lng });
        if (mapRef.current) {
            mapRef.current.panTo({ lat: loc.lat, lng: loc.lng });
        }
        if (onSearchArea) {
            onSearchArea({ lat: loc.lat, lng: loc.lng });
        }
    };

    useEffect(() => {
        if (landingLocation) {
            setMapCenter(landingLocation);
            setLoading(false);
            return;
        }

        getUserLocation().then((loc) => {
            setLocation({ lat: loc.lat, lng: loc.lng });
            setMapCenter({ lat: loc.lat, lng: loc.lng });
            setLoading(false);
        });
    }, [landingLocation]);

    if (loadError) return <Typography textAlign="center" color="white">Error loading maps</Typography>;
    if (!isLoaded || loading) return <LoadingComponent fullScreen={false} />;

    return (
        <Box
            sx={{
                height: height,
                overflow: "hidden",
                borderRadius: 2,
                position: "relative",
            }}
        >
            {showSearchButton && (
                <Button
                    variant="contained"              
                    sx={{
                        position: "absolute",
                        top: 20,
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 10,
                        borderRadius: 5,
                        background: theme.colors.secondary
                    }}
                    onClick={() => {
                        if (onSearchArea) {
                            onSearchArea(mapCenter);
                        }
                        setShowSearchButton(false);
                    }}
                >
                    Search this area
                </Button>
            )}
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={mapCenter || defaultLocation}
                zoom={ 14}
                options={{
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                }}
                disableDefaultUI={true}
                onLoad={(map) => (mapRef.current = map)}
                onIdle={() => {
                    if (!mapRef.current) return;
                    const newCenter = {
                        lat: mapRef.current.getCenter().lat(),
                        lng: mapRef.current.getCenter().lng(),
                    };
                    // Check if center is different from initial location to show search button
                    let isSame = false;
                    if (location) {
                        isSame =
                            Math.abs(newCenter.lat - location.lat) < 0.0001 &&
                            Math.abs(newCenter.lng - location.lng) < 0.0001;
                    }
                    setMapCenter(newCenter);
                    setShowSearchButton(!isSame);
                }}
            >
                {markers.map((marker, idx) => (
                  <MarkerF key={idx} position={{ lat: marker.lat, lng: marker.lng }} />
                ))}
            </GoogleMap>
            <Tooltip title="Recenter" arrow>
                <Fab
                    size="small"
                    sx={{
                        position: "absolute",
                        bottom: 20,
                        left: 20,
                        backgroundColor: "white",
                        color: "black",
                        "&:hover": {
                            backgroundColor: "#f0f0f0",
                        },
                    }}
                    onClick={handleRecenter}
                >
                    <MyLocationIcon />
                </Fab>
            </Tooltip>
        </Box>
    );
}

export default ExploreMapView;

ExploreMapView.propTypes = {
  height: PropTypes.object,
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
      label: PropTypes.string,
      slug: PropTypes.string
    })
  ),
  onSearchArea: PropTypes.func,
  landingLocation: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
};